import React, { useState, useEffect } from "react";
import styles from "./WorkBagForm.module.scss";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PDF from "../../components/Footer/PDF/Terminos-y-condiciones-edify-2021.pdf";
import { ReactComponent as CheckIcon } from "../../assets/svg/check-white.svg";
import { getCities } from "../../store/actions";
import { connect } from "react-redux";
import {
  ChevronDown,
  Check,
  X,
  Search,
} from "lucide-react";
import { JURISDICTIONS } from "../../constants/jurisdictions.ts";
import WaveInput from "../../components/UI/WaveInput/WaveInput.tsx";
import type { FormikProps } from "formik";
import type { WorkBagFormValues } from "../../types/workBag.ts";
interface WorkBagFormProps {
  formik: FormikProps<WorkBagFormValues>;
  skills: { id: number; name: string }[];
  cities: { name: string }[];
  today: string;
  isPhone: boolean;
  scrollToOffers?: () => void;
  getCities: (id: number) => void;
  captchaReady?: boolean;
  useCaptcha?: boolean;
  validatingCaptcha?: boolean;
}

const variants = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0 },
};

const WorkBagForm: React.FC<WorkBagFormProps> = React.memo(
  ({
    formik,
    skills,
    cities,
    today,
    isPhone,
    scrollToOffers,
    getCities,
    captchaReady = true,
    useCaptcha = false,
    validatingCaptcha = false,
  }) => {
    const [showJurisdictionList, setShowJurisdictionList] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
    const [onHover, setOnHover] = useState<string | number>("");
    const [citySearchValue, setCitySearchValue] = useState("");
    const [skillsSearchValue, setSkillsSearchValue] = useState("");
    const jurisdictionArray = JURISDICTIONS;

    const filteredSkills = skills?.filter((rubro) =>
      rubro.name.toLowerCase().includes(skillsSearchValue.toLowerCase())
    );
    const displaySkills = skillsSearchValue
      ? filteredSkills
      : skills ?? [];

    const handleAddSkill = (skillName: string, skillId: number) => {
      const skillExists = formik.values.skills.some((s) => s.id === skillId);
      if (!skillExists) {
        formik.setFieldValue("skills", [
          ...formik.values.skills,
          { id: skillId, name: skillName },
        ]);
      }
      setSkillsSearchValue("");
    };

    const filteredCities = cities?.filter((city) =>
      city.name.toLowerCase().includes(citySearchValue.toLowerCase())
    );
    const displayCities = citySearchValue ? filteredCities : cities ?? [];

    const selectedJurisdictionId = formik.values.selectedJurisdiction?.id;
    useEffect(() => {
      if (formik.values.selectedJurisdiction) {
        getCities(selectedJurisdictionId as number);
      }
    }, [formik.values.selectedJurisdiction, getCities, selectedJurisdictionId]);

    useEffect(() => {
      setCitySearchValue("");
      setShowCityDropdown(false);
    }, [formik.values.selectedJurisdiction]);

    const convertDateToISOFormat = (date: string) => {
      const [dd, mm, yyyy] = date.split("/");
      return `${yyyy}-${mm}-${dd}`;
    };

    const handleMouseEnter = (index: string | number) => setOnHover(index);
    const handleMouseLeave = () => setOnHover("");
    const handleTouchStart = (index: string | number) => setOnHover(index);
    const handleTouchEnd = () => setOnHover("");

    const isSkillSelected = (skill: { id: number }) =>
      formik.values.skills?.some((s) => s.id === skill.id) || false;

    const removeSkill = (skillToRemove: { id: number }) => {
      const newSkills = formik.values.skills.filter(
        (s) => s.id !== skillToRemove.id
      );
      formik.setFieldValue("skills", newSkills);
    };

    return (
      <motion.div
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.workBag}
      >
        <div className={styles.workBag_main}>
          <form onSubmit={formik.handleSubmit} className={styles.workBag_form}>
            <div className={styles.section}>
              <h2 className={styles.workBag_subtitle}>
                Tu información
              </h2>

              <div className={styles.formGroup}>
                <div className={styles.formGroup_textInputContainer}>
                  <WaveInput
                    id="name"
                    name="name"
                    label="Nombre"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={!!formik.errors.name}
                    errorMessage={
                      typeof formik.errors.name === "string"
                        ? formik.errors.name
                        : undefined
                    }
                  />
                </div>
                <div className={styles.formGroup_textInputContainer}>
                  <WaveInput
                    id="lastname"
                    name="lastname"
                    label="Apellido"
                    value={formik.values.lastname}
                    onChange={formik.handleChange}
                    error={!!formik.errors.lastname}
                    errorMessage={
                      typeof formik.errors.lastname === "string"
                        ? formik.errors.lastname
                        : undefined
                    }
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <div className={styles.formGroup_textInputContainer}>
                  <WaveInput
                    id="email"
                    name="email"
                    type="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={!!formik.errors.email}
                    errorMessage={
                      typeof formik.errors.email === "string"
                        ? formik.errors.email
                        : undefined
                    }
                  />
                </div>
                <div className={styles.formGroup_textInputContainer}>
                  <WaveInput
                    id="phone"
                    name="phone"
                    label="Teléfono"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    error={!!formik.errors.phone}
                    errorMessage={
                      typeof formik.errors.phone === "string"
                        ? formik.errors.phone
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.workBag_subtitle}>
                Rubros
              </h2>

              <div
                className={styles.formGroup_skills}
                onMouseEnter={() => handleMouseEnter(1)}
                onMouseLeave={handleMouseLeave}
                onTouchStart={() => handleTouchStart(1)}
                onTouchEnd={handleTouchEnd}
              >
                {onHover === 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className={styles.workBag_infoContainer}
                  >
                    <p className={styles.workBag_infoText}>
                      Seleccioná los rubros en los que te desempeñás, podés
                      elegir más de 1.
                    </p>
                  </motion.div>
                )}

                {formik.values.skills.length > 0 && (
                  <div className={styles.selectedItems}>
                    {formik.values.skills.map((skill, index) => (
                      <div key={index} className={styles.selectedItem}>
                        <span className={styles.selectedItemText}>
                          {skill.name}
                        </span>
                        <button
                          type="button"
                          className={styles.selectedItemRemove}
                          onClick={() => removeSkill(skill)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className={styles.skillsSearchWrapper}
                  onBlur={() =>
                    setTimeout(() => setShowSkillsDropdown(false), 150)
                  }
                >
                  <div
                    className={`${styles.skillsSearchInput} ${formik.errors.skills ? styles.error : ""}`}
                  >
                    <Search
                      className={styles.skillsSearchIcon}
                      size={20}
                    />
                    <input
                      type="text"
                      id="skills"
                      name="skills"
                      placeholder={
                        formik.errors.skills
                          ? String(formik.errors.skills)
                          : "Buscar rubro (ej: electricidad, plomería)"
                      }
                      value={skillsSearchValue}
                      className={styles.skillsSearchInput_field}
                      onChange={(e) =>
                        setSkillsSearchValue(e.target.value)
                      }
                      onFocus={() => setShowSkillsDropdown(true)}
                    />
                  </div>

                  {showSkillsDropdown && (
                    <div className={styles.skillsDropdown}>
                      {displaySkills.length > 0 ? (
                        displaySkills.map((skill) => (
                          <div
                            key={skill.id}
                            className={`${styles.dropdownItem} ${isSkillSelected(skill) ? styles.dropdownItemSelected : ""}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleAddSkill(skill.name, skill.id);
                            }}
                          >
                            <span className={styles.dropdownItemText}>
                              {skill.name}
                            </span>
                            {isSkillSelected(skill) ? (
                              <Check className={styles.dropdownItemCheck} />
                            ) : (
                              <span className={styles.dropdownItemAdd}>+</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className={styles.skillsDropdownEmpty}>
                          No se encontraron rubros
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.workBag_subtitle}>
                Zona de trabajo
              </h2>

              <div
                className={styles.formGroup_workZone}
                onMouseEnter={() => handleMouseEnter(2)}
                onMouseLeave={handleMouseLeave}
                onTouchStart={() => handleTouchStart(2)}
                onTouchEnd={handleTouchEnd}
              >
                {onHover === 2 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className={styles.workBag_infoContainer}
                  >
                    <p className={styles.workBag_infoText}>
                      Elegí la provincia y la ciudad donde estás dispuesto a
                      trabajar, podés elegir más de 1.
                    </p>
                  </motion.div>
                )}

                {formik.values.workZone.length > 0 && (
                  <div className={styles.selectedItems}>
                    {formik.values.workZone.map((workZone, index) => (
                      <div key={index} className={styles.selectedItem}>
                        <span className={styles.selectedItemText}>
                          {workZone.name} - {workZone.city.split(",")[0]}
                        </span>
                        <button
                          type="button"
                          className={styles.selectedItemRemove}
                          onClick={() => {
                            formik.setFieldValue(
                              "workZone",
                              formik.values.workZone.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.formGroup_workZone_inputsContainer}>
                  <div className={styles.inputWrapper}>
                    {isPhone ? (
                      <select
                        name="province"
                        value={formik.values.selectedJurisdiction?.id || ""}
                        onChange={(e) => {
                          const province = jurisdictionArray?.find(
                            (j) => j.id === Number.parseInt(e.target.value)
                          );
                          if (province) {
                            formik.setFieldValue("selectedJurisdiction", {
                              id: province.id,
                              name: province.name,
                            });
                          }
                        }}
                        className={`${styles.select} ${formik.values.workZone.length === 0 && !formik.isValid ? styles.error : ""}`}
                      >
                        <option value="">Seleccionar provincia</option>
                        {jurisdictionArray?.map((jurisdiction) => (
                          <option
                            key={jurisdiction.id}
                            value={jurisdiction.id}
                          >
                            {jurisdiction.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className={styles.customDropdown}>
                        <div
                          className={`${styles.workBag_workZoneContainer} ${formik.values.workZone.length === 0 && !formik.isValid ? styles.error : ""}`}
                        >
                          <input
                            type="text"
                            id="jurisdictionList"
                            name="jurisdictionList"
                            placeholder={
                              formik.values.workZone.length === 0 && !formik.isValid
                                ? String(formik.errors.workZone)
                                : "Elegir provincia"
                            }
                            className={`${styles.workBag_workZoneContainer_input} ${formik.values.workZone.length === 0 && !formik.isValid ? styles.error : ""}`}
                            value={formik.values.selectedJurisdiction?.name || ""}
                            readOnly
                          />
                          <ChevronDown
                            className={`${styles.dropdownIcon} ${showJurisdictionList ? styles.dropdownIconOpen : ""}`}
                            onClick={() =>
                              setShowJurisdictionList(!showJurisdictionList)
                            }
                          />
                        </div>

                        {showJurisdictionList && (
                          <div className={styles.dropdownMenu}>
                            {jurisdictionArray.map((jurisdiction) => (
                              <div
                                key={jurisdiction.id}
                                className={`${styles.dropdownItem} ${formik.values.selectedJurisdiction?.id === jurisdiction.id ? styles.dropdownItemSelected : ""}`}
                                onClick={() => {
                                  formik.setFieldValue("selectedJurisdiction", {
                                    id: jurisdiction.id,
                                    name: jurisdiction.name,
                                  });
                                  setShowJurisdictionList(false);
                                }}
                              >
                                <span className={styles.dropdownItemText}>
                                  {jurisdiction.name}
                                </span>
                                {formik.values.selectedJurisdiction?.id ===
                                  jurisdiction.id && (
                                  <Check
                                    className={styles.dropdownItemCheck}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={styles.inputWrapper}>
                    {isPhone ? (
                      <select
                        name="city"
                        value=""
                        onChange={(e) => {
                          const cityName = e.target.value;
                          if (
                            cityName &&
                            formik.values.selectedJurisdiction
                          ) {
                            const isCityDuplicate =
                              formik.values.workZone.some(
                                (zone) =>
                                  zone.city === cityName &&
                                  zone.jurisdiccion ===
                                    formik.values.selectedJurisdiction?.id
                              );

                            if (!isCityDuplicate) {
                              const newWorkZone = [
                                ...formik.values.workZone,
                                {
                                  jurisdiccion:
                                    formik.values.selectedJurisdiction.id,
                                  name: formik.values.selectedJurisdiction.name,
                                  city: cityName,
                                },
                              ];
                              formik.setFieldValue("workZone", newWorkZone);
                              formik.setFieldValue(
                                "selectedJurisdiction",
                                null
                              );
                            }
                          }
                        }}
                        disabled={!formik.values.selectedJurisdiction}
                        className={`${styles.select} ${!formik.values.selectedJurisdiction ? styles.selectDisabled : ""}`}
                      >
                        <option value="">Seleccionar ciudad</option>
                        {cities?.map((city, index) => (
                          <option key={index} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div
                        className={styles.citySearchWrapper}
                        onBlur={() =>
                          setTimeout(() => setShowCityDropdown(false), 150)
                        }
                      >
                        <div
                          className={`${styles.skillsSearchInput} ${!formik.values.selectedJurisdiction ? styles.disabledInput : ""}`}
                        >
                          <Search
                            className={styles.skillsSearchIcon}
                            size={20}
                          />
                          <input
                            type="text"
                            id="cityList"
                            name="cityList"
                            placeholder={
                              formik.values.selectedJurisdiction
                                ? "Buscar ciudad (ej: Córdoba, La Plata)"
                                : "Seleccionar provincia primero"
                            }
                            value={citySearchValue}
                            className={styles.skillsSearchInput_field}
                            onChange={(e) =>
                              setCitySearchValue(e.target.value)
                            }
                            onFocus={() =>
                              formik.values.selectedJurisdiction &&
                              setShowCityDropdown(true)
                            }
                            disabled={!formik.values.selectedJurisdiction}
                          />
                        </div>

                        {showCityDropdown &&
                          formik.values.selectedJurisdiction && (
                            <div className={styles.skillsDropdown}>
                              {displayCities.length > 0 ? (
                                displayCities.map((city) => {
                                  const isDuplicate =
                                    formik.values.workZone.some(
                                      (zone) =>
                                        zone.city === city.name &&
                                        zone.jurisdiccion ===
                                          formik.values.selectedJurisdiction?.id
                                    );
                                  return (
                                    <div
                                      key={city.name}
                                      className={`${styles.dropdownItem} ${isDuplicate ? styles.dropdownItemDisabled : ""}`}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (!isDuplicate && formik.values.selectedJurisdiction) {
                                          const newWorkZone = [
                                            ...formik.values.workZone,
                                            {
                                              jurisdiccion:
                                                formik.values.selectedJurisdiction.id,
                                              name: formik.values
                                                .selectedJurisdiction.name,
                                              city: city.name,
                                            },
                                          ];
                                          formik.setFieldValue(
                                            "workZone",
                                            newWorkZone
                                          );
                                          formik.setFieldValue(
                                            "selectedJurisdiction",
                                            null
                                          );
                                          setCitySearchValue("");
                                        }
                                      }}
                                    >
                                      <span className={styles.dropdownItemText}>
                                        {city.name}
                                      </span>
                                      {isDuplicate ? (
                                        <Check
                                          className={styles.dropdownItemCheck}
                                        />
                                      ) : (
                                        <span
                                          className={styles.dropdownItemAdd}
                                        >
                                          +
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className={styles.skillsDropdownEmpty}>
                                  No se encontraron ciudades
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.workBag_subtitle}>
                Situación laboral
              </h2>

              <div className={styles.radioGroup}>
                <div className={styles.radioItem}>
                  <span className={styles.radioLabel}>
                    ¿Posee libreta de fondo de desempleo?
                  </span>
                  <div className={styles.radioOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="unemploymentFund"
                        value="true"
                        checked={formik.values.unemploymentFund === true}
                        onChange={() =>
                          formik.setFieldValue("unemploymentFund", true)
                        }
                      />
                      <span className={styles.radioCustom} />
                      Sí
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="unemploymentFund"
                        value="false"
                        checked={formik.values.unemploymentFund === false}
                        onChange={() =>
                          formik.setFieldValue("unemploymentFund", false)
                        }
                      />
                      <span className={styles.radioCustom} />
                      No
                    </label>
                  </div>
                  {formik.errors.unemploymentFund &&
                    formik.touched.unemploymentFund && (
                      <span className={styles.errorText}>
                        {formik.errors.unemploymentFund}
                      </span>
                    )}
                </div>

                <div className={styles.radioItem}>
                  <span className={styles.radioLabel}>
                    ¿Tiene trabajo actualmente?
                  </span>
                  <div className={styles.radioOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="currentJob"
                        value="true"
                        checked={formik.values.currentJob === true}
                        onChange={() =>
                          formik.setFieldValue("currentJob", true)
                        }
                      />
                      <span className={styles.radioCustom} />
                      Sí
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="currentJob"
                        value="false"
                        checked={formik.values.currentJob === false}
                        onChange={() =>
                          formik.setFieldValue("currentJob", false)
                        }
                      />
                      <span className={styles.radioCustom} />
                      No
                    </label>
                  </div>
                  {formik.errors.currentJob && formik.touched.currentJob && (
                    <span className={styles.errorText}>
                      {formik.errors.currentJob}
                    </span>
                  )}
                </div>

                {formik.values.currentJob && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className={styles.inputWrapper}
                  >
                    <label
                      htmlFor="employmentEndDate"
                      className={styles.dateLabel}
                    >
                      Fecha aprox. de finalización de empleo
                    </label>
                    <input
                      type="date"
                      id="employmentEndDate"
                      name="employmentEndDate"
                      min={convertDateToISOFormat(today)}
                      className={`${styles.workBag_input} ${formik.errors.employmentEndDate ? styles.error : ""}`}
                      onChange={formik.handleChange}
                      value={formik.values.employmentEndDate}
                    />
                    {formik.errors.employmentEndDate && (
                      <span className={styles.errorText}>
                        {formik.errors.employmentEndDate}
                      </span>
                    )}
                  </motion.div>
                )}
              </div>
            </div>

            <div className={styles.section}>
              <label className={styles.checkboxWrapper}>
                <input
                  id="termsAndConditions"
                  name="termsAndConditions"
                  type="checkbox"
                  onChange={formik.handleChange}
                  className={styles.checkboxInput}
                />
                <span className={styles.checkboxCustom}>
                  {formik.values.termsAndConditions && <Check size={14} />}
                </span>
                <span className={styles.checkboxLabel}>
                  Acepto los{" "}
                  <Link to={PDF} className={styles.link} target="_blank" download>
                    Términos y Condiciones*
                  </Link>
                </span>
              </label>
              {formik.touched.termsAndConditions &&
                formik.errors.termsAndConditions && (
                  <span className={styles.errorText}>
                    {formik.errors.termsAndConditions}
                  </span>
                )}
            </div>

            <div className={styles.buttonsContainer}>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={
                  !formik.isValid ||
                  !formik.dirty ||
                  formik.isSubmitting ||
                  !captchaReady
                }
              >
                <CheckIcon className={styles.checkIcon} />
                {formik.isSubmitting
                  ? useCaptcha
                    ? "Verificando..."
                    : "Enviando..."
                  : validatingCaptcha
                    ? "Validando..."
                    : "Enviar"}
              </button>
            </div>

            {isPhone && scrollToOffers && (
              <div className={styles.buttonsContainer}>
                <button
                  type="button"
                  onClick={scrollToOffers}
                  className={styles.secondaryButton}
                >
                  Ver búsquedas abiertas
                </button>
              </div>
            )}
          </form>
        </div>
      </motion.div>
    );
  }
);

WorkBagForm.displayName = "WorkBagForm";

const mapStateToProps = (state: { register: { cities: { name: string }[] } }) => ({
  cities: state.register.cities,
});

export default connect(mapStateToProps, { getCities })(WorkBagForm);
